import React, { useState, useEffect } from 'react';
import { Study } from '../types';
import { getPublicStudies } from '../services/firebase';
import { Icons } from './ui/Icons';
import { formatAuthorName } from '../utils/formatters';
import { Pagination, getPaginatedItems, getTotalPages, ITEMS_PER_PAGE } from './ui/Pagination';

interface PublicGalleryProps {
  onStudySelect: (study: Study) => void;
  embedded?: boolean;
}

export const PublicGallery: React.FC<PublicGalleryProps> = ({ onStudySelect, embedded = false }) => {
  const [studies, setStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const loadPublicStudies = async () => {
      try {
        setLoading(true);
        const publicStudies = await getPublicStudies();
        setStudies(publicStudies);
      } catch (err) {
        console.error('Failed to load public studies:', err);
        setError('Failed to load studies');
      } finally {
        setLoading(false);
      }
    };

    loadPublicStudies();
  }, []);

  if (loading) {
    return (
      <div className={embedded ? "py-12 flex justify-center" : "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center"}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? "py-12 text-center" : "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6"}>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "w-full" : "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50"}>
      {/* Content */}
      <div className={embedded ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {studies.length === 0 ? (
          <div className="text-center py-12">
            <Icons.BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Public Studies Yet</h2>
            <p className="text-gray-500 mb-6">Be the first to create and share a public study!</p>
            <button
              onClick={() => {}}
              className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Get Started
            </button>
          </div>
        ) : (
          <div>
            {!embedded && (
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {studies.length} {studies.length === 1 ? 'Study' : 'Studies'} Available
              </h2>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getPaginatedItems(studies, currentPage).map((study) => (
                <div
                  key={study.id}
                  onClick={() => onStudySelect(study)}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer overflow-hidden group"
                >
                  {/* Cover Image */}
                  {study.coverImage ? (
                    <div className="h-48 overflow-hidden relative">
                      <img
                        src={study.coverImage}
                        alt={study.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        style={{
                          objectPosition: study.coverImagePosition
                            ? `${study.coverImagePosition.x}% ${study.coverImagePosition.y}%`
                            : 'center'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Icons.BookOpen className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      {study.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {study.description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center text-xs text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Icons.BookOpen className="w-3.5 h-3.5" />
                        {study.segments?.length || 0} segments
                      </span>
                    </div>

                    {/* Tags */}
                    {study.tags && study.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {study.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {study.tags.length > 3 && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{study.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {/* View Button */}
                    <button
                      className="w-full py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-bold text-sm shadow-md flex items-center justify-center gap-2 group-hover:bg-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        onStudySelect(study);
                      }}
                    >
                      Start Reading Now <Icons.Next className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={getTotalPages(studies.length)}
              totalItems={studies.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
              itemLabel="studies"
            />
          </div>
        )}
      </div>
    </div>
  );
};
